import { colors } from '@kmns-internal/furikake'
import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Stack from '@mui/material/Stack'
import { alpha } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import {
  ResponseTypeEnum,
  TemplateNodeSchema,
  TemplateQuestion,
} from '@ulysses-inc/harami_api_client'
import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { HeaderBackButtonMui } from 'src/components/header/HeaderBackButtonMui'
import {
  applyColumnQuestionTypeChange,
  DEFAULT_ROW_HEADING_NAME,
  getQuestionLogicNodes,
  getTableColumnNodes,
  getTableDisplayColumns,
  getTableFixedRowSectionNodes,
  getTableTemplateRowNode,
  TABLE_LOGIC_MAX_DEPTH,
  type ColumnInputType,
  type TableBranchColumn,
  type TableDisplayColumn,
  type TableHeaderBandCell,
} from 'src/features/templateEdit/section/table/model'
import { AppBackgroundColor } from 'src/features/theme/KdsThemeColor'
import {
  addTableBranchQuestion as addTableBranchQuestionAction,
  addTableColumn as addTableColumnAction,
  appendTableRow as appendTableRowAction,
  changeActiveNodeId as changeActiveNodeIdAction,
  deleteTableBranchQuestion as deleteTableBranchQuestionAction,
  deleteTableColumn as deleteTableColumnAction,
  deleteTableLogic as deleteTableLogicAction,
  deleteTemplateNode as deleteTemplateNodeAction,
  insertTableRow as insertTableRowAction,
  moveTableColumn as moveTableColumnAction,
  updateTemplateNode as updateTemplateNodeAction,
} from 'src/state/ducks/templates/actions'
import { showEditDeviateComment } from 'src/state/ducks/templates/editDeviateComment/actions'
import {
  showEditMultipleChoiceLogic,
  showEditNumberLogic,
} from 'src/state/ducks/templates/editLogic/actions'
import type { RootState } from 'src/state/store'
import DeleteConfirmDialog from 'src/views/components/dialogs/DeleteConfirmDialog'
import { TableColumnSettings, TableRowHeadingSettings } from './columnSettings'
import { TablePreview, type TableEditHandlers } from './preview'
import type { TableActionMenuItem } from './TableActionMenu'
import { TableRowHeadingHideConfirmDialog } from './TableRowHeadingHideConfirmDialog'

type Props = {
  tableNode: TemplateNodeSchema
  activeNodeId: number
}

/**
 * 表編集のフルスクリーン Dialog の z-index。
 * オプション編集の Drawer/Modal(MUI のデフォルトは 1000 以上)より下に置くことで、
 * それらを各コンポーネント側で前面化せずともこの Dialog の上に開ける。
 */
const TABLE_EDIT_DIALOG_Z_INDEX = 999

/**
 * MULTIPLE_CHOICE / NUMBER の列だけが条件分岐を持てる。
 * NUMBER はルール(subType)が設定されている場合のみ「正常/違反」で分岐でき、
 * 未設定なら分岐の概念が成立しないため対象外とする(スクロール形式と挙動を揃える)。
 */
const supportsLogic = (question?: TemplateQuestion): boolean => {
  switch (question?.responseType) {
    case ResponseTypeEnum.MULTIPLE_CHOICE:
      return true
    case ResponseTypeEnum.NUMBER:
      return !!(question.responseNumbers ?? [])[0]?.subType
    default:
      return false
  }
}

/** 設定パネル/数値ルールドロワーで共有する、選択中のノード(列 or 分岐質問)。 */
type SelectedTableNode = {
  node: TemplateNodeSchema
  /** 条件分岐の深さ。列は 0、その分岐質問は 1。 */
  depth: number
}

/** activeNodeId に一致する表示される列(列 or 分岐質問)を探す純粋関数。 */
const findSelectedTableNode = (
  displayColumns: TableDisplayColumn[],
  activeNodeId: number,
): SelectedTableNode | null => {
  for (const column of displayColumns) {
    if (column.kind === 'column' && column.columnId === activeNodeId) {
      return { node: column.question, depth: 0 }
    }
    if (column.kind === 'branch' && column.question?.id === activeNodeId) {
      return { node: column.question, depth: column.conditions.length }
    }
  }
  return null
}

export const TableSectionEditor = ({ tableNode, activeNodeId }: Props) => {
  const dispatch = useDispatch()
  const [isEditing, setIsEditing] = useState(false)

  const templateNodes = useSelector(
    (state: RootState) => state.templatesState.templatePages.templateNodes,
  )

  // 表系の更新は reducer が templateNodes を新参照で返すため、これらの useMemo は
  // 表操作のたびに再計算される(全体再計算)。派生コストは小さいので許容している。
  const columns = useMemo(
    () => getTableColumnNodes(tableNode, templateNodes),
    [tableNode, templateNodes],
  )
  const displayColumns = useMemo(
    () => getTableDisplayColumns(tableNode, templateNodes),
    [tableNode, templateNodes],
  )
  const rows = useMemo(
    () => getTableFixedRowSectionNodes(tableNode, templateNodes),
    [tableNode, templateNodes],
  )
  const templateRow = useMemo(
    () => getTableTemplateRowNode(tableNode, templateNodes),
    [tableNode, templateNodes],
  )
  // 表セクションはセクションの見せ方の 1 つなので、表示 / 非表示を保存する項目は持たない。見出し名の有無から初期値を決め、画面を開いている間だけ持つ。
  const [rowHeadingHidden, setRowHeadingHidden] = useState(
    () => !templateRow.section?.name?.trim(),
  )
  const [isRowHeadingHideConfirmOpen, setIsRowHeadingHideConfirmOpen] =
    useState(false)

  // 選択中ノード(列 or 分岐質問)。設定パネルとドロワーで共有する。
  // 安価な導出なので手動 useMemo は使わず、React Compiler のメモ化に委ねる。
  const selected = findSelectedTableNode(displayColumns, activeNodeId)
  const selectedRowHeading = rowHeadingHidden
    ? undefined
    : [templateRow, ...rows].find(row => row.id === activeNodeId)
  const rowHeadingName = templateRow.section?.name?.trim() ?? ''

  const requireLogicNode = (logicId: number): TemplateNodeSchema => {
    const logicNode = templateNodes[logicId]
    if (!logicNode) {
      throw new Error(`条件分岐ノード ${logicId} が見つかりません`)
    }
    return logicNode
  }

  const addColumnAt = (insertIndex: number) => {
    dispatch(
      addTableColumnAction(
        tableNode,
        templateNodes,
        ResponseTypeEnum.TEXT,
        insertIndex,
      ),
    )
  }

  const insertColumn = (anchorColumnId: number, position: 'left' | 'right') => {
    const anchorIndex = columns.findIndex(
      column => column.id === anchorColumnId,
    )
    if (anchorIndex < 0) {
      return
    }
    addColumnAt(position === 'right' ? anchorIndex + 1 : anchorIndex)
  }

  const addColumnAtEnd = () => addColumnAt(columns.length)

  const selectColumn = (questionId: number) => {
    dispatch(changeActiveNodeIdAction(questionId))
  }

  const deleteColumn = (column: TemplateNodeSchema) => {
    DeleteConfirmDialog({
      title: '列を削除してもよろしいですか？',
      content: column.nodes.length
        ? '紐づく条件分岐列も一緒に削除されます。'
        : '',
      onOk: () =>
        dispatch(deleteTableColumnAction(tableNode, templateNodes, column.id)),
    })
  }

  const reorderColumn = (fromIndex: number, toIndex: number) => {
    dispatch(
      moveTableColumnAction(tableNode, templateNodes, fromIndex, toIndex),
    )
  }

  const openLogic = (column: TemplateNodeSchema) => {
    const responseType = column.question?.responseType
    if (responseType === ResponseTypeEnum.MULTIPLE_CHOICE) {
      dispatch(showEditMultipleChoiceLogic(column))
    } else if (responseType === ResponseTypeEnum.NUMBER) {
      dispatch(showEditNumberLogic(column))
    }
  }

  const addBranchQuestion = (logicId: number) => {
    dispatch(
      addTableBranchQuestionAction(requireLogicNode(logicId), templateNodes),
    )
  }

  const deleteBranchQuestion = (branch: TableBranchColumn) => {
    if (!branch.question) {
      return
    }
    const branchQuestion = branch.question
    const hasNestedLogic =
      getQuestionLogicNodes(branchQuestion, templateNodes).length > 0
    DeleteConfirmDialog({
      title: '分岐質問を削除してもよろしいですか？',
      content: hasNestedLogic ? '紐づく条件分岐列も一緒に削除されます。' : '',
      onOk: () =>
        dispatch(
          deleteTableBranchQuestionAction(
            templateNodes,
            branchQuestion.id,
            requireLogicNode(branch.logicId),
            branch.parentQuestionId,
          ),
        ),
    })
  }

  const addRow = () => {
    dispatch(appendTableRowAction(tableNode, templateNodes))
  }

  const insertRow = (anchorRowId: number, position: 'above' | 'below') => {
    dispatch(
      insertTableRowAction(tableNode, templateNodes, anchorRowId, position),
    )
  }

  const deleteRow = (rowId: number) => {
    dispatch(deleteTemplateNodeAction(rowId, tableNode))
  }

  const updateRowName = (row: TemplateNodeSchema, name: string) => {
    dispatch(
      updateTemplateNodeAction(row.id, {
        ...row,
        section: { ...row.section, name },
      }),
    )
  }

  // 見出しを表示している表では見出し名を空にできない。見出し名の有無で見出し列を出すかが決まるため。
  const fillRowHeadingNameIfEmpty = () => {
    if (!templateRow.section?.name?.trim()) {
      updateRowName(templateRow, DEFAULT_ROW_HEADING_NAME)
    }
  }

  // 表示 / 非表示の設定は保存しないので、見出し名を空にして見出しのない表にする。見出しのない表に使われない行の見出しを残さないよう、行の見出しも空にする。
  const hideRowHeading = () => {
    updateRowName(templateRow, '')
    rows.forEach(row => updateRowName(row, ''))
    setRowHeadingHidden(true)
    setIsRowHeadingHideConfirmOpen(false)
  }

  // 見出し名が「見出し」のままで行の見出しもすべて空なら、非表示にしても破棄されるものはない。
  const isRowHeadingUnset = () =>
    rowHeadingName === DEFAULT_ROW_HEADING_NAME &&
    rows.every(row => !row.section?.name?.trim())

  // 行を選択中にすると findSelectedTableNode が null を返し、下部パネルの列設定が閉じる。
  const selectRowHeading = (rowId: number) => {
    dispatch(changeActiveNodeIdAction(rowId))
  }

  const updateColumn = (
    node: TemplateNodeSchema,
    patch: Partial<TemplateQuestion>,
  ) => {
    const nextNode = {
      ...node,
      question: { ...node.question, ...patch },
    }
    dispatch(updateTemplateNodeAction(nextNode.id, nextNode))
  }

  const updateColumnType = (
    node: TemplateNodeSchema,
    responseType: ColumnInputType,
  ) => {
    const nextNode = applyColumnQuestionTypeChange(node, responseType)
    dispatch(updateTemplateNodeAction(nextNode.id, nextNode))
  }

  const buildColumnMenuItems = (
    column: TemplateNodeSchema,
  ): TableActionMenuItem[] => {
    // 条件分岐の設定は列設定パネル側のアイコンから行う（通常質問と同様）
    return [
      {
        key: 'add-column-left',
        label: '左に列を追加',
        onClick: () => insertColumn(column.id, 'left'),
      },
      {
        key: 'add-column-right',
        label: '右に列を追加',
        onClick: () => insertColumn(column.id, 'right'),
      },
      {
        key: 'delete-column',
        label: '列を削除',
        danger: true,
        disabled: columns.length <= 1,
        onClick: () => deleteColumn(column),
      },
    ]
  }

  const buildBranchMenuItems = (
    branch: TableBranchColumn,
  ): TableActionMenuItem[] => {
    if (!branch.question) {
      return []
    }
    return [
      {
        key: 'add-branch-question',
        label: '分岐質問を追加',
        onClick: () => addBranchQuestion(branch.logicId),
      },
      {
        key: 'delete-branch-question',
        label: '分岐質問を削除',
        danger: true,
        onClick: () => deleteBranchQuestion(branch),
      },
    ]
  }

  const buildRowMenuItems = (rowId: number): TableActionMenuItem[] => [
    {
      key: 'add-row-above',
      label: '上に行を追加',
      onClick: () => insertRow(rowId, 'above'),
    },
    {
      key: 'add-row-below',
      label: '下に行を追加',
      onClick: () => insertRow(rowId, 'below'),
    },
    {
      key: 'delete-row',
      label: '行を削除',
      danger: true,
      onClick: () => deleteRow(rowId),
    },
  ]

  const deleteLogic = (band: TableHeaderBandCell) => {
    DeleteConfirmDialog({
      title: '条件分岐列を削除してもよろしいですか？',
      content: band.hasBranchQuestion ? '紐づく質問も一緒に削除されます。' : '',
      onOk: () =>
        dispatch(
          deleteTableLogicAction(
            templateNodes,
            band.logicId,
            band.parentQuestionId,
          ),
        ),
    })
  }

  const handlers: TableEditHandlers = {
    onSelectColumn: selectColumn,
    onReorderColumn: reorderColumn,
    buildColumnMenuItems,
    buildBranchMenuItems,
    onAddBranchQuestion: addBranchQuestion,
    onDeleteLogic: deleteLogic,
    buildRowMenuItems,
    onSelectRowHeading: selectRowHeading,
    onToggleRowHeadingHidden: () => {
      if (rowHeadingHidden) {
        fillRowHeadingNameIfEmpty()
        setRowHeadingHidden(false)
      } else if (isRowHeadingUnset()) {
        hideRowHeading()
      } else {
        setIsRowHeadingHideConfirmOpen(true)
      }
    },
  }

  const openEditor = () => {
    setIsEditing(true)
    dispatch(changeActiveNodeIdAction(tableNode.id))
  }

  const closeEditor = () => {
    if (!rowHeadingHidden) {
      fillRowHeadingNameIfEmpty()
    }
    setIsEditing(false)
  }

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: colors.gray[1],
        borderLeft: 1,
        borderRight: 1,
        borderColor: 'divider',
        pb: 1,
      }}
    >
      {/* プレビューは一定の領域を確保し、グレーのオーバーレイで覆う。領域全体がクリックで編集を開く。 */}
      <Box
        onClick={openEditor}
        sx={theme => ({
          position: 'relative',
          isolation: 'isolate',
          height: theme.spacing(35),
          overflow: 'hidden',
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: colors.white,
          cursor: 'pointer',
        })}
      >
        {displayColumns.length > 0 && (
          <TablePreview
            displayColumns={displayColumns}
            templateRow={templateRow}
            rowHeadingHidden={rowHeadingHidden}
            rows={rows}
            templateNodes={templateNodes}
            activeNodeId={activeNodeId}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            // sticky なヘッダー(zIndex 2〜3)より前面に出してオーバーレイで覆う。
            zIndex: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(colors.gray[5], 0.25),
          }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={event => {
              event.stopPropagation()
              openEditor()
            }}
            sx={{
              fontWeight: 600,
              // オーバーレイ上で背景が透けないよう、ホバー時も不透明を保つ。
              bgcolor: colors.white,
              '&:hover': { bgcolor: colors.white },
            }}
          >
            表を編集する
          </Button>
        </Box>
      </Box>

      <Dialog
        fullScreen
        open={isEditing}
        onClose={closeEditor}
        disableEnforceFocus
        sx={{ zIndex: TABLE_EDIT_DIALOG_Z_INDEX }}
      >
        <Stack sx={{ height: '100%', bgcolor: AppBackgroundColor }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={theme => ({
              height: theme.spacing(9),
              px: 3,
              bgcolor: colors.white,
              boxShadow: 1,
            })}
          >
            <HeaderBackButtonMui onClick={closeEditor} />
            <Typography variant="h6" fontWeight={700}>
              表を編集
            </Typography>
          </Stack>

          <Box
            sx={theme => ({
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateRows: `minmax(0, 1fr) minmax(${theme.spacing(40)}, auto)`,
            })}
          >
            <Box
              sx={{
                minHeight: 0,
                overflow: 'auto',
                p: 3,
                bgcolor: AppBackgroundColor,
              }}
            >
              <TablePreview
                displayColumns={displayColumns}
                templateRow={templateRow}
                rowHeadingHidden={rowHeadingHidden}
                rows={rows}
                templateNodes={templateNodes}
                activeNodeId={activeNodeId}
                interactive
                showAddColumn
                onAddColumn={addColumnAtEnd}
                handlers={handlers}
              />
              <Stack direction="row" sx={{ mt: 1 }}>
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={addRow}
                  aria-label="行を追加する"
                >
                  追加する
                </Button>
              </Stack>
            </Box>

            <Box
              sx={{
                minHeight: 0,
                overflow: 'auto',
                bgcolor: colors.white,
                borderTop: 1,
                borderColor: 'divider',
                boxShadow: 3,
              }}
            >
              {selected ? (
                <TableColumnSettings
                  key={selected.node.id}
                  columnNode={selected.node}
                  onUpdateColumn={patch => updateColumn(selected.node, patch)}
                  onChangeType={responseType =>
                    updateColumnType(selected.node, responseType)
                  }
                  updateColumnNode={(nodeId, changeNode) =>
                    dispatch(updateTemplateNodeAction(nodeId, changeNode))
                  }
                  showEditDeviateComment={node =>
                    dispatch(showEditDeviateComment(node))
                  }
                  onOpenLogic={
                    // 条件分岐を持てるのは NUMBER / MULTIPLE_CHOICE の質問。
                    selected.depth < TABLE_LOGIC_MAX_DEPTH &&
                    supportsLogic(selected.node.question)
                      ? () => openLogic(selected.node)
                      : undefined
                  }
                />
              ) : selectedRowHeading ? (
                <TableRowHeadingSettings
                  key={selectedRowHeading.id}
                  {...(selectedRowHeading.id === templateRow.id
                    ? {
                        title: rowHeadingName,
                        label: '見出し名',
                        onBlur: fillRowHeadingNameIfEmpty,
                      }
                    : {
                        title: `${rowHeadingName} - ${rows.indexOf(selectedRowHeading) + 1}行目`,
                        label: '見出しに表示するテキスト',
                      })}
                  value={selectedRowHeading.section?.name ?? ''}
                  onChange={name => updateRowName(selectedRowHeading, name)}
                />
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ p: 4, height: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary">
                    列を選択すると設定を編集できます
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>
        </Stack>
      </Dialog>
      <TableRowHeadingHideConfirmDialog
        open={isRowHeadingHideConfirmOpen}
        onCancel={() => setIsRowHeadingHideConfirmOpen(false)}
        onOk={hideRowHeading}
      />
    </Box>
  )
}
